import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { History, User, Calendar } from 'lucide-react';

function TaskHistory({ taskId, taskName, isOpen, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchHistory();
    }
  }, [isOpen, taskId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/tasks/${taskId}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching task history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      name: 'Nome',
      status: 'Status',
      deadline: 'Prazo',
      assigned_to: 'Responsável',
      weight: 'Peso',
      priority: 'Prioridade',
      notes: 'Observações'
    };
    return labels[field] || field;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'not_started': 'Não Iniciada',
      'in_progress': 'Em Andamento',
      'completed': 'Concluída',
      'cancelled': 'Cancelada'
    };
    return labels[status] || status;
  };

  const formatValue = (field, value) => {
    if (!value || value === 'None') return '-';
    if (field === 'status') return getStatusLabel(value);
    if (field === 'weight') return `${value}%`;
    return value;
  };

  const getChangeColor = (field, newValue) => {
    if (field === 'status') {
      if (newValue === 'completed') return 'bg-green-100 text-green-700';
      if (newValue === 'cancelled') return 'bg-red-100 text-red-700';
      if (newValue === 'in_progress') return 'bg-blue-100 text-blue-700';
    }
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Histórico de Alterações
          </DialogTitle>
          <p className="text-sm text-gray-600">{taskName}</p>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-800"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma alteração registrada</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => (
                <div 
                  key={entry.history_id} 
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`history-entry-${entry.history_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-medium">
                        {getFieldLabel(entry.field_changed)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {new Date(entry.changed_at).toLocaleString('pt-BR')}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 p-2 bg-red-50 rounded border border-red-200">
                      <p className="text-xs text-red-600 mb-1">Antes</p>
                      <p className="text-sm font-medium text-red-800">
                        {formatValue(entry.field_changed, entry.old_value)}
                      </p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    <div className="flex-1 p-2 bg-green-50 rounded border border-green-200">
                      <p className="text-xs text-green-600 mb-1">Depois</p>
                      <p className="text-sm font-medium text-green-800">
                        {formatValue(entry.field_changed, entry.new_value)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="w-4 h-4" />
                    <span>Alterado por <strong>{entry.changed_by_name}</strong></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default TaskHistory;
